import { useProductManagementColumnConfiguration } from '@/activityLog/productsManagement/Inventory/hooks/useProductManagementColumnConfiguration.ts';
import { ConfigurableTable } from 'common/components/ConfigurableTable/ConfigurableTable.tsx';
import {
  CounterCell,
  CounterCellFailed,
  CounterCellSuccess,
} from '@/activityLog/bulkProvisioning/Components/Cells/CounterCell.tsx';
import { StatusBadgesCell } from '@/activityLog/bulkProvisioning/Components/Cells/StatusBadgesCell.tsx';
import { DateTimeCell } from 'common/components/ConfigurableTable/Components/Cells/DateTimeCell.tsx';
import { TextCell } from 'common/components/ConfigurableTable/Components/Cells/TextCell.tsx';

import {
  productsManagementPageFilterAtom,
  productsManagementRowsPerPageFilterAtom,
} from 'atoms/filters/products-management-filters.ts';
import { useAtomPaginationChange } from 'common/components/ConfigurableTable/hooks/useAtomPaginationChange.ts';
import { useProductsManagementFetch } from '@/activityLog/productsManagement/Inventory/hooks/useProductsManagementFetch.ts';
import { TableSkeleton } from 'common/components/Table/TableSkeleton.tsx';
import { ProductsManagementNoRowsOverlay } from '@/activityLog/productsManagement/Inventory/ProductsManagementNoRowsOverlay.tsx';
import { useIsFetching } from '@tanstack/react-query';
import { productsManagementQueries } from '@/activityLog/productsManagement/queries/productsManagementQueries.ts';
import { useHasFeatureFlag } from 'featureFlags/useHasFeatureFlag.ts';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { DetailsPanel } from 'common/components/ConfigurableTable/Components/Containers/DetailsPanel.tsx';
import { ProductManagementErrorsPanel } from '@/activityLog/productsManagement/Errors/ProductManagementErrorsPanel.tsx';
import { DropzoneWrapper } from '@/activityLog/common/Upload/DropzoneWrapper.tsx';
import { useProductsManagementUpload } from '@/activityLog/productsManagement/Inventory/hooks/useProductsManagementUpload.ts';
import { UploadDialog } from '@/activityLog/bulkProvisioning/Components/UploadDialog.tsx';
import { UploadSnackbar } from '@/activityLog/common/Upload/UploadSnackbar.tsx';

import { useProductsManagementRowActions } from '@/activityLog/productsManagement/Inventory/hooks/useProductsManagementRowActions.tsx';

const getDetailPanelContent = (params: GridRowParams) => {
  return params.row.numberOfFailedCards > 0 ? (
    <DetailsPanel>
      <ProductManagementErrorsPanel requestId={params.row.id as string} />
    </DetailsPanel>
  ) : undefined;
};

const getDetailPanelHeight = () => 'auto' as const;

export const ProductsManagementTable = () => {
  const configuration = useProductManagementColumnConfiguration();

  const { page, rowsPerPage, onPaginationChange } = useAtomPaginationChange({
    pageAtom: productsManagementPageFilterAtom,
    rowsPerPageAtom: productsManagementRowsPerPageFilterAtom,
  });

  const { data } = useProductsManagementFetch();

  const isFetching =
    useIsFetching({
      queryKey: productsManagementQueries.listAll(),
    }) > 0;

  const errorsViewEnabled = useHasFeatureFlag('ProductManagmentExperimentalErrorsView');

  const { selectedFile, selectFile, uploadFile, importError, setImportError, isPending } =
    useProductsManagementUpload();

  const productsManagementRowActions = useProductsManagementRowActions();

  return isFetching ? (
    <TableSkeleton showEndSkeleton />
  ) : (
    <>
      <DropzoneWrapper onDrop={selectFile} data-testid="product management drop area">
        <ConfigurableTable
          disableColumnReorder
          actions={productsManagementRowActions}
          data-testid="products management table"
          configuration={configuration}
          rows={data.items}
          cellConfiguration={{
            numberOfCards: CounterCell,
            numberOfSuccessCards: CounterCellSuccess,
            numberOfFailedCards: CounterCellFailed,
            status: StatusBadgesCell,
            uploadAt: DateTimeCell,
            uploadFinishedAt: DateTimeCell,
            default: TextCell,
          }}
          totalNumberOfItems={data.totalNumberOfItems}
          onPaginationChange={onPaginationChange}
          page={page}
          pageSize={rowsPerPage}
          slots={{
            noRowsOverlay: ProductsManagementNoRowsOverlay,
          }}
          getDetailPanelContent={errorsViewEnabled ? getDetailPanelContent : undefined}
          getDetailPanelHeight={errorsViewEnabled ? getDetailPanelHeight : undefined}
        />
      </DropzoneWrapper>
      <UploadDialog
        open={!!selectedFile && !importError}
        fileName={selectedFile?.name || ''}
        onConfirm={() => {
          if (isPending || importError) {
            return;
          }
          uploadFile();
        }}
        onCancel={() => {
          selectFile(null);
        }}
      />
      <UploadSnackbar
        importError={importError}
        onClose={function (): void {
          setImportError(null);
          selectFile(null);
        }}
      />
    </>
  );
};
